import { useEffect, useState } from 'react';
import { contentApi } from '../../services/api';

interface NavSubmenuItem {
  id: string;
  title: string;
  titleEn: string;
  path: string;
  active: boolean;
}
interface NavMenuItem {
  id: string;
  title: string;
  titleEn: string;
  active: boolean;
  submenu?: NavSubmenuItem[];
}

export default function NavConfigEditor() {
  const [items, setItems] = useState<NavMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await contentApi.getNavConfig();
      if (res.data?.data?.items) setItems(res.data.data.items);
    } catch (e) {
      console.error('Failed to load nav config', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await contentApi.saveNavConfig({ items });
      setDirty(false);
    } catch (e) {
      console.error('Failed to save nav config', e);
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (idx: number, field: string, value: any) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setDirty(true);
  };

  const updateSubItem = (menuIdx: number, subIdx: number, field: string, value: any) => {
    setItems(prev => {
      const next = [...prev];
      const submenu = [...(next[menuIdx].submenu || [])];
      submenu[subIdx] = { ...submenu[subIdx], [field]: value };
      next[menuIdx] = { ...next[menuIdx], submenu };
      return next;
    });
    setDirty(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Configurar Menu de Navegacion</h2>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className={`px-4 py-2 rounded text-sm font-medium ${
            dirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>

      <div className="space-y-4">
        {items.map((item, idx) => (
          <div key={item.id} className="border rounded-lg dark:border-gray-700 overflow-hidden">
            {/* Menu item header */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.active}
                  onChange={(e) => updateItem(idx, 'active', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="text-xs text-gray-500 uppercase">Activo</span>
              </label>
              <input
                type="text"
                value={item.title}
                onChange={(e) => updateItem(idx, 'title', e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Titulo (ES)"
              />
              <input
                type="text"
                value={item.titleEn}
                onChange={(e) => updateItem(idx, 'titleEn', e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Title (EN)"
              />
              <button
                onClick={() => setExpandedMenu(expandedMenu === item.id ? null : item.id)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2"
              >
                {expandedMenu === item.id ? '▲' : '▼'} {item.submenu?.length || 0} items
              </button>
            </div>

            {/* Submenu items */}
            {expandedMenu === item.id && item.submenu && (
              <div className="divide-y dark:divide-gray-700">
                {item.submenu.map((sub, subIdx) => (
                  <div key={sub.id} className="flex items-center gap-3 px-4 py-3 pl-10">
                    <label className="flex items-center gap-2 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={sub.active}
                        onChange={(e) => updateSubItem(idx, subIdx, 'active', e.target.checked)}
                        className="w-4 h-4 rounded"
                      />
                    </label>
                    <input
                      type="text"
                      value={sub.title}
                      onChange={(e) => updateSubItem(idx, subIdx, 'title', e.target.value)}
                      className="flex-1 px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Titulo (ES)"
                    />
                    <input
                      type="text"
                      value={sub.titleEn}
                      onChange={(e) => updateSubItem(idx, subIdx, 'titleEn', e.target.value)}
                      className="flex-1 px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Title (EN)"
                    />
                    <input
                      type="text"
                      value={sub.path}
                      onChange={(e) => updateSubItem(idx, subIdx, 'path', e.target.value)}
                      className="w-48 px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-xs"
                      placeholder="/ruta"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {!dirty && (
        <p className="mt-4 text-sm text-gray-400 text-center">
          Los cambios se reflejan en la web de la organizacion al guardar.
        </p>
      )}
    </div>
  );
}
